
import React from 'react';
import { useAttachments } from '@/context/AttachmentContext/useAttachments';
import TopSendersChart from './charts/TopSendersChart';
import FileTypesChart from './charts/FileTypesChart';
import AnalyticsSkeletons from './charts/AnalyticsSkeletons';
import { simplifyFileType } from '@/utils/fileTypeUtils';

const AttachmentAnalytics: React.FC = () => {
  const { filteredAttachments, isLoading } = useAttachments();

  // Transform data for "Most Popular Senders" chart
  const senderData = React.useMemo(() => {
    if (!filteredAttachments.length) return [];
    
    const senderCounts: Record<string, number> = {};
    
    filteredAttachments.forEach(attachment => {
      const sender = attachment.emailSender;
      senderCounts[sender] = (senderCounts[sender] || 0) + 1;
    });
    
    // Convert to array and sort by count
    return Object.entries(senderCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5); // Get top 5
  }, [filteredAttachments]);
  
  // Transform data for "Most Popular File Types" chart
  const fileTypeData = React.useMemo(() => {
    if (!filteredAttachments.length) return [];
    
    const typeCounts: Record<string, number> = {};
    
    filteredAttachments.forEach(attachment => {
      // Use the utility function to get a simplified file type
      const simpleType = simplifyFileType(attachment.fileType, attachment.fileName);
      typeCounts[simpleType] = (typeCounts[simpleType] || 0) + 1;
    });
    
    // Convert to array and sort by count
    return Object.entries(typeCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5); // Get top 5
  }, [filteredAttachments]);

  if (isLoading) {
    return <AnalyticsSkeletons />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <TopSendersChart data={senderData} />
      <FileTypesChart data={fileTypeData} />
    </div>
  );
};

export default AttachmentAnalytics;
